/**
 * CreateUserWizard — 4-step onboarding flow for creating an ISO or Agent.
 * Replaces the previous single-step "Create Team Member" modal.
 *
 * Steps:
 *   1. Basic Info      — name, email, phone, home + personal address, SSN, EIN (masked, encrypted server-side)
 *   2. Entity          — legal business name, DBA, office address, phone, website, service-area zip codes
 *   3. Schedule A      — per-line markup on the parent's cost basis (SwiftISO-style; tabbed by category)
 *   4. Permissions     — togglable feature/tab checklist, defaults pre-selected per role
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Check, X, User, Building2, FileText, ShieldCheck } from 'lucide-react';
import api from '../../lib/api';
import useAuthStore from '../../lib/auth-store';

const STEPS = [
  { id: 1, label: 'Basic Info', icon: User },
  { id: 2, label: 'Entity', icon: Building2 },
  { id: 3, label: 'Schedule A', icon: FileText },
  { id: 4, label: 'Permissions', icon: ShieldCheck },
];

const emptyAddr = { street: '', city: '', state: '', zip: '', country: 'US' };

export default function CreateUserWizard({ open, onClose, onCreated, defaultRole = 'iso_agent' }) {
  const { user: currentUser } = useAuthStore();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Determine allowable child roles based on current user role.
  const roleOptions = useMemo(() => {
    if (currentUser?.role === 'super_admin') return [{ value: 'iso', label: 'ISO' }];
    if (currentUser?.role === 'iso') return [{ value: 'iso_agent', label: 'ISO Agent' }];
    return [];
  }, [currentUser?.role]);

  const [form, setForm] = useState({
    // Step 1
    first_name: '', last_name: '', email: '', phone: '', password: '',
    role: defaultRole,
    personal: {
      full_name: '', ssn: '', ein: '',
      personal_address: { ...emptyAddr },
      home_address: { ...emptyAddr },
    },
    // Step 2
    entity: {
      legal_business_name: '', dba: '', phone: '', website: '',
      office_address: { ...emptyAddr },
      service_area_zips: [],
    },
    // Step 3
    schedule_a_markup: { processor_slug: 'maverick', markup_lines: [] },
    // Step 4
    permissions_overrides: {},
  });

  useEffect(() => {
    if (open && roleOptions.length && !roleOptions.some(o => o.value === form.role)) {
      setForm(f => ({ ...f, role: roleOptions[0].value }));
    }
  }, [open, roleOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (path, value) => {
    setForm((f) => {
      const clone = JSON.parse(JSON.stringify(f));
      const parts = path.split('.');
      let cur = clone;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = value;
      return clone;
    });
  };

  const canNext = () => {
    if (step === 1) return form.first_name && form.email && form.role;
    if (step === 2) return form.entity.legal_business_name;
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email.trim().toLowerCase(),
        phone: form.phone || null,
        password: form.password || null,
        role: form.role,
        personal: cleanEmpty(form.personal),
        entity: cleanEmpty(form.entity),
        permissions: { overrides: form.permissions_overrides },
      };
      if (form.schedule_a_markup.markup_lines.length > 0) {
        payload.schedule_a_markup = form.schedule_a_markup;
      }
      const r = await api.wizardCreateUser(payload);
      toast.success('Team member created successfully');
      if (r.temporary_password) {
        toast.info(`Temporary password: ${r.temporary_password}`, { duration: 15000 });
      }
      onCreated?.(r);
      onClose();
    } catch (e) {
      toast.error(e.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="create-user-wizard">
        <DialogHeader>
          <DialogTitle>Create Team Member</DialogTitle>
          <DialogDescription>
            Onboard a new {form.role === 'iso' ? 'ISO' : 'agent'} — basic info, entity details, Schedule A markup, and permissions.
          </DialogDescription>
        </DialogHeader>

        <StepIndicator step={step} />

        <div className="min-h-[380px]">
          {step === 1 && <StepBasic form={form} update={update} roleOptions={roleOptions} />}
          {step === 2 && <StepEntity form={form} update={update} />}
          {step === 3 && <StepScheduleA form={form} setForm={setForm} />}
          {step === 4 && <StepPermissions form={form} setForm={setForm} />}
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            data-testid="wizard-back-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            {step < 4 ? (
              <Button onClick={() => setStep(step + 1)} disabled={!canNext()} data-testid="wizard-next-btn">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={saving} data-testid="wizard-submit-btn">
                {saving ? 'Creating…' : 'Create Team Member'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Step Indicator ─────────────────────────────────────────────

function StepIndicator({ step }) {
  return (
    <div className="flex items-center justify-between py-2 border-b mb-4">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const isActive = s.id === step;
        const isDone = s.id < step;
        return (
          <React.Fragment key={s.id}>
            <div className="flex items-center gap-2">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center border-2 ${
                isActive ? 'border-primary bg-primary text-primary-foreground' :
                isDone ? 'border-primary bg-primary/20 text-primary' : 'border-muted-foreground/30 text-muted-foreground'
              }`}>
                {isDone ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
              </div>
              <span className={`text-xs font-medium ${isActive ? '' : 'text-muted-foreground'} hidden sm:inline`}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${isDone ? 'bg-primary' : 'bg-muted-foreground/20'}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Utility ────────────────────────────────────────────────────

function cleanEmpty(obj) {
  // Strip empty strings and empty nested addresses so backend doesn't overwrite with blanks
  if (Array.isArray(obj)) return obj;
  if (obj && typeof obj === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      const cleaned = cleanEmpty(v);
      if (cleaned === '' || cleaned === null || cleaned === undefined) continue;
      if (typeof cleaned === 'object' && !Array.isArray(cleaned) && Object.keys(cleaned).length === 0) continue;
      out[k] = cleaned;
    }
    return out;
  }
  return obj;
}

// ── Step 1: Basic Info ─────────────────────────────────────────

function StepBasic({ form, update, roleOptions }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="First Name *" testId="wiz-first-name">
          <Input value={form.first_name} onChange={(e) => update('first_name', e.target.value)} />
        </Field>
        <Field label="Last Name" testId="wiz-last-name">
          <Input value={form.last_name} onChange={(e) => update('last_name', e.target.value)} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Email *" testId="wiz-email">
          <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} />
        </Field>
        <Field label="Phone">
          <Input value={form.phone} onChange={(e) => update('phone', e.target.value)} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Role">
          <Select value={form.role} onValueChange={(v) => update('role', v)}>
            <SelectTrigger data-testid="wiz-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {roleOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Password (blank = auto-generated)">
          <Input type="password" value={form.password} onChange={(e) => update('password', e.target.value)} />
        </Field>
      </div>

      <div className="border-t pt-4 mt-2">
        <p className="text-sm font-semibold mb-3">Personal Details (KYC)</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="SSN (encrypted)">
            <Input placeholder="123-45-6789" value={form.personal.ssn}
              onChange={(e) => update('personal.ssn', e.target.value)} data-testid="wiz-ssn" />
          </Field>
          <Field label="EIN (encrypted)">
            <Input placeholder="12-3456789" value={form.personal.ein}
              onChange={(e) => update('personal.ein', e.target.value)} data-testid="wiz-ein" />
          </Field>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">
          Encrypted at rest; only Super Admin can unmask for downline users, with password re-authentication.
        </p>

        <p className="text-sm font-semibold mt-4 mb-2">Home Address</p>
        <AddressGrid addr={form.personal.home_address}
          onChange={(a) => update('personal.home_address', a)} testPrefix="wiz-home" />
      </div>
    </div>
  );
}

function AddressGrid({ addr, onChange, testPrefix = 'addr' }) {
  const set = (k, v) => onChange({ ...addr, [k]: v });
  return (
    <div className="grid grid-cols-6 gap-2">
      <div className="col-span-4"><Input placeholder="Street" value={addr.street || ''} onChange={(e) => set('street', e.target.value)} data-testid={`${testPrefix}-street`} /></div>
      <div className="col-span-2"><Input placeholder="City" value={addr.city || ''} onChange={(e) => set('city', e.target.value)} /></div>
      <div className="col-span-2"><Input placeholder="State" value={addr.state || ''} onChange={(e) => set('state', e.target.value)} /></div>
      <div className="col-span-2"><Input placeholder="ZIP" value={addr.zip || ''} onChange={(e) => set('zip', e.target.value)} /></div>
      <div className="col-span-2"><Input placeholder="Country" value={addr.country || 'US'} onChange={(e) => set('country', e.target.value)} /></div>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="text-xs mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

// ── Step 2: Entity ─────────────────────────────────────────────

function StepEntity({ form, update }) {
  const [zipInput, setZipInput] = useState('');
  const addZip = () => {
    if (!zipInput.trim()) return;
    const next = [...(form.entity.service_area_zips || []), zipInput.trim()];
    update('entity.service_area_zips', Array.from(new Set(next)));
    setZipInput('');
  };
  const removeZip = (z) => update('entity.service_area_zips',
    (form.entity.service_area_zips || []).filter((x) => x !== z));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Legal Business Name *" testId="wiz-legal-name">
          <Input value={form.entity.legal_business_name} onChange={(e) => update('entity.legal_business_name', e.target.value)} />
        </Field>
        <Field label="DBA / Trade Name">
          <Input value={form.entity.dba} onChange={(e) => update('entity.dba', e.target.value)} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Business Phone">
          <Input value={form.entity.phone} onChange={(e) => update('entity.phone', e.target.value)} />
        </Field>
        <Field label="Website">
          <Input placeholder="https://…" value={form.entity.website} onChange={(e) => update('entity.website', e.target.value)} />
        </Field>
      </div>

      <div>
        <p className="text-sm font-semibold mb-2">Office Address</p>
        <AddressGrid addr={form.entity.office_address}
          onChange={(a) => update('entity.office_address', a)} testPrefix="wiz-office" />
      </div>

      <div>
        <p className="text-sm font-semibold mb-2">Service-Area Reach (ZIP codes)</p>
        <div className="flex gap-2">
          <Input
            placeholder="Add a ZIP code (e.g. 33101)"
            value={zipInput}
            onChange={(e) => setZipInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addZip(); } }}
            data-testid="wiz-zip-input"
          />
          <Button variant="outline" onClick={addZip}>Add</Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {(form.entity.service_area_zips || []).map((z) => (
            <Badge key={z} variant="secondary" className="pl-3 pr-1 py-0.5">
              {z}
              <button type="button" className="ml-1 hover:text-red-500" onClick={() => removeZip(z)}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {(form.entity.service_area_zips || []).length === 0 && (
            <p className="text-xs text-muted-foreground">No service area set — leave blank for national coverage.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Step 3: Schedule A Markup ──────────────────────────────────

const CATEGORY_TABS = [
  'Card Processing',
  'Card Processing - Authorization Fees',
  'Card Processing - Cardholder Data',
  'Card Processing - Chargebacks & Support',
  'ACH',
  'Other Services',
];

function StepScheduleA({ form, setForm }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const tpls = await api.getProcessorCostTemplates(form.schedule_a_markup.processor_slug);
        // Only the "Standard" active templates
        const active = (tpls || []).filter(t => t.status === 'active');
        setTemplates(active);
        if (active.length && !templateId) setTemplateId(active[0].id);
      } catch (e) {
        // ignore
      }
    })();
  }, [form.schedule_a_markup.processor_slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedTemplate = templates.find(t => t.id === templateId);
  const costLines = selectedTemplate?.cost_lines || [];

  // Group by category
  const categories = useMemo(() => {
    const groups = new Map();
    for (const l of costLines) {
      const cat = l.category || 'Other';
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat).push(l);
    }
    return Array.from(groups.entries());
  }, [costLines]);

  const markupByCode = useMemo(() => {
    const m = {};
    for (const line of form.schedule_a_markup.markup_lines || []) m[line.fee_code] = line;
    return m;
  }, [form.schedule_a_markup.markup_lines]);

  const setMarkup = (fee_code, unit, amount) => {
    setForm((f) => {
      const others = (f.schedule_a_markup.markup_lines || []).filter(l => l.fee_code !== fee_code);
      const nextLines = amount > 0
        ? [...others, { fee_code, unit, markup_amount: parseFloat(amount) }]
        : others;
      return { ...f, schedule_a_markup: { ...f.schedule_a_markup, markup_lines: nextLines } };
    });
  };

  // Effective sell rate = hard_cost + markup (same unit)
  const effectiveSell = (line) => {
    const m = markupByCode[line.fee_code];
    const markup = m ? parseFloat(m.markup_amount) : 0;
    return line.amount + markup;
  };

  const visibleCategories = activeTab === 'all'
    ? categories
    : categories.filter(([c]) => c.toLowerCase().includes(activeTab.toLowerCase()));

  // Estimated agent spread (rough demo assumption: $100K volume, 1000 transactions per merchant)
  const estimatedSpread = useMemo(() => {
    const V = 100000, N = 1000;
    let total = 0;
    for (const line of form.schedule_a_markup.markup_lines || []) {
      if (line.unit === 'per_transaction') total += line.markup_amount * N;
      else if (line.unit === 'bps') total += V * (line.markup_amount / 10000);
      else if (line.unit === 'flat_monthly') total += line.markup_amount;
      else if (line.unit === 'flat_annual') total += line.markup_amount / 12;
    }
    return total;
  }, [form.schedule_a_markup.markup_lines]);

  return (
    <div className="space-y-3" data-testid="wiz-schedule-a">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Processor">
          <Select value={form.schedule_a_markup.processor_slug}
            onValueChange={(v) => setForm(f => ({ ...f, schedule_a_markup: { ...f.schedule_a_markup, processor_slug: v } }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="maverick">TSYS</SelectItem>
              <SelectItem value="fiserv">Fiserv</SelectItem>
              <SelectItem value="epx">Global/EPX</SelectItem>
              <SelectItem value="cygma">Cygma</SelectItem>
              <SelectItem value="square">Square</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Cost Template">
          <Select value={templateId} onValueChange={setTemplateId}>
            <SelectTrigger><SelectValue placeholder="Pick a template…" /></SelectTrigger>
            <SelectContent>
              {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>

      {costLines.length === 0 ? (
        <div className="text-sm text-muted-foreground p-6 text-center border rounded-lg">
          Pick a processor & cost template above to configure line-item markup.
          <br />You can skip this step and add Schedule A later.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1 border-b pb-2">
            <TabPill active={activeTab === 'all'} onClick={() => setActiveTab('all')}>All ({costLines.length})</TabPill>
            {categories.map(([c, lines]) => (
              <TabPill key={c} active={activeTab === c} onClick={() => setActiveTab(c)}>
                {c} ({lines.length})
              </TabPill>
            ))}
          </div>

          <div className="max-h-[380px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background">
                <tr className="text-left border-b text-muted-foreground text-xs">
                  <th className="py-2">Billing Item</th>
                  <th className="text-right">Hard Cost (Buy Rate)</th>
                  <th className="text-right">Added Markup</th>
                  <th className="text-right">Effective Sell Rate</th>
                </tr>
              </thead>
              <tbody>
                {visibleCategories.map(([cat, lines]) => (
                  <React.Fragment key={cat}>
                    <tr className="bg-muted/40">
                      <td colSpan={4} className="py-1 px-2 font-semibold uppercase text-[10px] tracking-wide">
                        {cat}
                      </td>
                    </tr>
                    {lines.map((line) => {
                      const m = markupByCode[line.fee_code];
                      const isBps = line.unit === 'bps';
                      const sell = effectiveSell(line);
                      return (
                        <tr key={line.fee_code} className="border-b border-dashed last:border-0" data-testid={`markup-row-${line.fee_code}`}>
                          <td className="py-2">
                            <div className="font-medium">{line.label || line.fee_code}</div>
                            {line.billing_basis && <div className="text-[10px] text-muted-foreground">{line.billing_basis}</div>}
                          </td>
                          <td className="text-right font-mono text-xs">
                            {isBps ? `${line.amount} bps` : `$${line.amount.toFixed(4)}`}
                          </td>
                          <td className="text-right">
                            <div className="inline-flex items-center gap-1">
                              <span className="text-[10px] text-muted-foreground">{isBps ? 'bps' : '$'}</span>
                              <Input
                                type="number"
                                step={0.0001}
                                value={m?.markup_amount ?? ''}
                                placeholder="0.00"
                                className="h-8 w-24 text-right"
                                onChange={(e) => setMarkup(line.fee_code, line.unit, e.target.value)}
                              />
                            </div>
                          </td>
                          <td className="text-right font-mono text-xs text-emerald-500 font-semibold">
                            {isBps ? `${sell.toFixed(2)} bps` : `$${sell.toFixed(4)}`}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg bg-primary/5 border border-primary/20">
            <div className="text-xs text-muted-foreground">
              Estimated {form.role === 'iso' ? 'ISO' : 'Agent'} Spread <span className="opacity-60">(assumes $100K volume, 1,000 transactions/mo)</span>
            </div>
            <div className="text-lg font-semibold" data-testid="estimated-spread">
              ${estimatedSpread.toFixed(2)} <span className="text-xs text-muted-foreground">/ merchant / month</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function TabPill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
        active ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/70 text-muted-foreground'
      }`}
    >
      {children}
    </button>
  );
}

// ── Step 4: Permissions ────────────────────────────────────────

function StepPermissions({ form, setForm }) {
  const [features, setFeatures] = useState({});
  useEffect(() => {
    api.getFeatureList().then(d => setFeatures(d.features || {})).catch(() => {});
  }, []);

  const featureEntries = Object.entries(features);
  // Effective = role default overridden by user pick
  const isEnabled = (key) => {
    if (form.permissions_overrides[key] !== undefined) return form.permissions_overrides[key];
    return features[key]?.[form.role] ?? false;
  };
  const toggle = (key) => {
    setForm((f) => ({
      ...f,
      permissions_overrides: {
        ...f.permissions_overrides,
        [key]: !isEnabled(key),
      },
    }));
  };
  const resetToDefaults = () => setForm(f => ({ ...f, permissions_overrides: {} }));

  return (
    <div className="space-y-3" data-testid="wiz-permissions">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Toggle which tabs & features this user can access. Defaults are pre-selected based on the role.
        </p>
        <Button variant="ghost" size="sm" onClick={resetToDefaults}>Reset to defaults</Button>
      </div>
      <div className="grid grid-cols-2 gap-2 max-h-[420px] overflow-y-auto">
        {featureEntries.map(([key, defaults]) => (
          <div key={key} className="flex items-center justify-between p-2 rounded border" data-testid={`perm-${key}`}>
            <div>
              <div className="text-sm font-medium capitalize">{key.replace(/_/g, ' ')}</div>
              <div className="text-[10px] text-muted-foreground">
                Default for {form.role.replace('_', ' ')}: {defaults[form.role] ? 'Allow' : 'Block'}
              </div>
            </div>
            <Switch checked={isEnabled(key)} onCheckedChange={() => toggle(key)} />
          </div>
        ))}
      </div>
    </div>
  );
}
